import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, X } from "lucide-react";
import AppShell from "../components/AppShell";
import { EmptyData, Spinner } from "../components/Shared";
import { api, gbp, catColor, fmtDate } from "../lib/api";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Transactions() {
    const [params, setParams] = useSearchParams();
    const month = params.get("month") || "";
    const category = params.get("category") || "";
    const [q, setQ] = useState(params.get("q") || "");
    const [data, setData] = useState(null);
    const [months, setMonths] = useState([]);

    const load = () => api.get("/transactions", { params: { month: month || undefined, category: category || undefined, q: q || undefined } }).then((r) => setData(r.data));
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => { load(); }, [month, category]);
    useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]);
    /* eslint-enable react-hooks/exhaustive-deps */
    useEffect(() => { api.get("/overview").then((r) => setMonths(r.data.months || [])); }, []);

    const setParam = (k, v) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); setParams(p); };

    const recat = async (tx, cat) => {
        try {
            const { data: r } = await api.patch(`/transactions/${tx.id}`, { category: cat, apply_to_merchant: true });
            toast.success(`${r.merchant} → ${cat} (all matching transactions)`);
            load();
        } catch (e) { toast.error("Could not update category"); }
    };

    if (!data) return <AppShell title="Transactions" subtitle="Every penny" testid="transactions-page"><Spinner /></AppShell>;
    const total = data.items.filter((t) => t.amount < 0 && !["Income", "Transfers & Savings"].includes(t.category)).reduce((s, t) => s - t.amount, 0);

    return (
        <AppShell title="Transactions" subtitle="Every penny" testid="transactions-page"
            actions={<p className="font-mono text-sm text-muted-foreground" data-testid="transactions-total">{data.items.length} shown · <span className="font-bold text-foreground">{gbp(total, 0)}</span> spent</p>}>
            <div className="mb-4 flex flex-wrap gap-2" data-testid="transactions-filters">
                <div className="relative min-w-[200px] flex-1">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search merchants…" data-testid="transactions-search" className="bg-card border-border pl-9" />
                </div>
                <Select value={month || "all"} onValueChange={(v) => setParam("month", v === "all" ? "" : v)}>
                    <SelectTrigger className="w-40 bg-card border-border" data-testid="transactions-month-select"><SelectValue placeholder="All months" /></SelectTrigger>
                    <SelectContent className="bg-card border-border"><SelectItem value="all">All months</SelectItem>{months.map((m) => <SelectItem key={m.month} value={m.month}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={category || "all"} onValueChange={(v) => setParam("category", v === "all" ? "" : v)}>
                    <SelectTrigger className="w-52 bg-card border-border" data-testid="transactions-category-select"><SelectValue placeholder="All categories" /></SelectTrigger>
                    <SelectContent className="bg-card border-border max-h-72"><SelectItem value="all">All categories</SelectItem>{data.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
                {(month || category || q) && <button onClick={() => { setQ(""); setParams({}); }} data-testid="transactions-clear" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"><X className="h-3.5 w-3.5" /> Clear</button>}
            </div>

            {data.items.length === 0 && !month && !category && !q ? <EmptyData onLoaded={load} /> : (
                <div className="overflow-hidden rounded-2xl border border-border bg-card" data-testid="transactions-table">
                    {data.items.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">No transactions match.</p>}
                    <ul className="divide-y divide-border">
                        {data.items.map((t, i) => (
                            <li key={t.id} className="flex items-center gap-3 px-4 py-3 hover:bg-secondary/40" data-testid={`tx-row-${i}`}>
                                <span className="w-14 shrink-0 font-mono text-xs text-muted-foreground">{fmtDate(t.date)}</span>
                                <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-medium text-foreground">{t.merchant}</p>
                                    <p className="truncate text-xs text-muted-foreground">{t.description}</p>
                                </div>
                                <Select value={t.category} onValueChange={(v) => recat(t, v)}>
                                    <SelectTrigger className="h-7 w-auto min-w-[9rem] gap-1.5 border-0 bg-transparent px-2 text-xs hover:bg-secondary" data-testid={`tx-category-${i}`}>
                                        <span className="h-2 w-2 rounded-full" style={{ background: catColor(t.category) }} /><SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-card border-border max-h-72">{data.categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                </Select>
                                <span className={`w-24 shrink-0 text-right font-mono text-sm font-semibold ${t.amount > 0 ? "text-emerald-400" : "text-foreground"}`} data-testid={`tx-amount-${i}`}>
                                    {t.amount > 0 ? "+" : ""}{gbp(t.amount)}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </AppShell>
    );
}
